import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CheckCircle } from 'lucide-react'
import { useArtworks, usePublishArtwork } from '../hooks/useArtworks'
import { ArtworkStatus } from '@/types/entities'
import { artworkApi } from '../api/artworkApi'
import { EmptyState } from '@/components/common/data-display/EmptyState'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { Pagination } from '@/components/common/navigation/Pagination'
import { truncate } from '@/lib/utils'
import { PAGINATION } from '@/config/constants'

export function PendingArtworksList() {
  const [page, setPage] = useState(1)
  const pageSize = PAGINATION.DEFAULT_PAGE_SIZE
  const navigate = useNavigate()

  const { data, isLoading, refetch } = useArtworks(ArtworkStatus.PENDING, {
    start: (page - 1) * pageSize,
    limit: pageSize,
  })

  const { mutate: publishArtwork } = usePublishArtwork()
  // Ids with an Approve in flight: their button is disabled so a second click cannot send again.
  const [approving, setApproving] = useState<Set<number>>(new Set())

  const handleApprove = (artworkId: number) => {
    if (approving.has(artworkId)) return
    setApproving((s) => new Set(s).add(artworkId))
    publishArtwork(artworkId, {
      onSuccess: () => refetch(),
      onSettled: () =>
        setApproving((s) => {
          const next = new Set(s)
          next.delete(artworkId)
          return next
        }),
    })
  }

  // Approve marks the cached artwork PUBLISHED straight away; leave it off the pending list then
  // rather than waiting for the refetch (the card used to stay, inviting a second click).
  const pending = (data?.data ?? []).filter((artwork) => artwork.status === ArtworkStatus.PENDING)

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!data || pending.length === 0) {
    return <EmptyState title="No pending artworks" description="All caught up!" />
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {pending.map((artwork) => (
          <Card key={artwork.id} className="overflow-hidden">
            <div
              className="aspect-square cursor-pointer"
              onClick={() => navigate(`/app/artworks/${artwork.id}`)}
            >
              <img
                src={artworkApi.getArtworkImageUrl(artwork.id)}
                alt={artwork.name}
                className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                onError={(e) => {
                  // Prevent infinite loop by checking if we're already showing the fallback
                  if (e.currentTarget.src.includes('placeholder-image')) {
                    return
                  }
                  e.currentTarget.src = '/placeholder-image.svg'
                }}
              />
            </div>
            <CardHeader>
              <CardTitle className="text-lg">{artwork.name}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {truncate(artwork.description || 'No description', 100)}
              </p>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Button
                onClick={() => handleApprove(artwork.id)}
                disabled={approving.has(artwork.id)}
                className="flex-1"
                size="sm"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/app/artworks/${artwork.id}`)}
                className="flex-1"
                size="sm"
              >
                Review
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {data.total > pageSize && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={data.total}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}

